import { useState, useEffect, useMemo } from 'react';
import { api } from '../lib/api.js';
import { fmtMoney } from '../lib/format.js';

// Weekly trends — tips, scheduled hours, actual hours, labor cost +
// labor pct by Mon-anchored ISO week × location for the past 12 weeks.
// Owners see every café; managers see only their assigned ones.
// CJ feedback 2026-05-09: lives under Reports → Weekly trends.
export function PayrollTrendsView({ currentUser }) {
  const headers = { 'x-user-id': String(currentUser.id) };
  const [rows, setRows] = useState([]);
  const [locations, setLocations] = useState([]);
  const [rollups, setRollups] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  useEffect(() => {
    setLoading(true);
    Promise.all([
      api('GET', '/api/admin/payroll/trends?weeks=12', null, headers),
      api('GET', '/api/locations'),
      api('GET', '/api/admin/payroll/rollups', null, headers).catch(() => ({ periods: null })),
    ]).then(([r, locs, ru]) => {
      setRows(Array.isArray(r?.weeks) ? r.weeks : []);
      setLocations(Array.isArray(locs) ? locs : []);
      setRollups(ru?.periods || null);
    }).catch((e) => setErr(e.message || String(e)))
      .finally(() => setLoading(false));
  }, []);

  // Group rows by week (rows is already sorted asc by week then loc).
  const byWeek = useMemo(() => {
    const m = new Map();
    for (const r of rows) {
      const arr = m.get(r.week_start) || [];
      arr.push(r);
      m.set(r.week_start, arr);
    }
    return Array.from(m.entries()).sort((a, b) => b[0].localeCompare(a[0])); // newest first
  }, [rows]);

  function locName(id) { return locations.find((l) => l.id === id)?.name || `#${id}`; }
  function fmtMoney(n) { return '$' + Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 }); }
  function fmtHours(n) { return Number(n || 0).toFixed(1) + 'h'; }
  function laborPct(r) {
    // Revenue now rides along from Tally (weekly-sales, joined by Square
    // location id) — real labor % at last: cost ÷ net sales. Null when Tally
    // didn't answer or the week predates its history.
    if (!r || !r.revenue || !(r.labor_cost > 0)) return null;
    return (r.labor_cost / r.revenue) * 100;
  }

  if (loading) return <div className="card text-sm text-gray-500">Loading trends…</div>;
  if (err) {
    let friendly = err;
    try { const p = JSON.parse(err); friendly = p?.error || err; } catch {}
    const isAuth = /authenticat/i.test(friendly);
    return (
      <div className="card" style={{ borderLeft: '4px solid #c62828' }}>
        <div className="text-sm font-semibold text-red-700">
          {isAuth ? 'Your session expired' : 'Could not load trends'}
        </div>
        <div className="text-xs text-gray-600 mt-1">
          {isAuth ? 'Refresh the page or sign in again and the report will reload.' : friendly}
        </div>
      </div>
    );
  }
  if (byWeek.length === 0) return (
    <div className="card text-sm text-gray-500">
      No data for the past 12 weeks. Punches + cash counts populate this view as pay periods complete.
    </div>
  );

  // Period rollup comparison card. CJ 2026-05-21.
  function pctDelta(curr, prior) {
    if (!Number.isFinite(curr) || !Number.isFinite(prior) || prior === 0) return null;
    return ((curr - prior) / prior) * 100;
  }
  function fmtPct(p) {
    if (p == null || !Number.isFinite(p)) return '';
    const sign = p > 0 ? '+' : '';
    return `${sign}${p.toFixed(1)}%`;
  }
  function pctColor(p, biggerIsBetter) {
    if (p == null || !Number.isFinite(p)) return '#9e9e9e';
    if (Math.abs(p) < 0.5) return '#9e9e9e';
    const up = p > 0;
    return (up === biggerIsBetter) ? '#16a34a' : '#dc2626';
  }
  function RollupRow({ label, current, prior, biggerIsBetter, formatter }) {
    const dPct = pctDelta(current, prior);
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 13 }}>
        <div>
          <strong>{label}</strong>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div>{formatter(current)} <span style={{ color: '#9e9e9e' }}>vs {formatter(prior)}</span></div>
          <div style={{ fontSize: 11, fontWeight: 600, color: pctColor(dPct, biggerIsBetter) }}>{fmtPct(dPct)}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {rollups && (rollups.current_quarter || rollups.current_year) && (
        <div className="card">
          <div className="text-base font-semibold mb-1">Period roll-ups</div>
          <div className="text-xs text-gray-500 mb-3">
            Quarter and year-to-date totals compared to the same point last period.
            Green = trending the favorable direction (more tips, fewer labor hours).
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16 }}>
            {['current_quarter', 'current_year'].filter(k => rollups[k]).map(k => {
              const cur = rollups[k];
              const priorKey = k === 'current_quarter' ? 'prior_quarter' : 'prior_year';
              const prior = rollups[priorKey] || { hours: 0, labor_cost: 0, tips: 0, label: '' };
              return (
                <div key={k} style={{ border: '1px solid #e0e0e0', borderRadius: 8, padding: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#757575', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                    {cur.label} <span style={{ color: '#bdbdbd', fontWeight: 400 }}>vs {prior.label}</span>
                  </div>
                  <RollupRow label="Labor cost"  current={cur.labor_cost} prior={prior.labor_cost} biggerIsBetter={false} formatter={fmtMoney} />
                  <RollupRow label="Actual hours" current={cur.hours}      prior={prior.hours}      biggerIsBetter={false} formatter={fmtHours} />
                  <RollupRow label="Tips"        current={cur.tips}        prior={prior.tips}       biggerIsBetter={true}  formatter={fmtMoney} />
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="card">
        <div className="text-base font-semibold mb-1">Weekly trends — last 12 weeks</div>
        <div className="text-xs text-gray-500">
          Tips and labor cost are reported per location. Scheduled hours come from the published schedule;
          actual hours are clocked-in time. Average labor rate ($/hr) is labor cost ÷ actual hours.
        </div>
      </div>

      {byWeek.map(([weekStart, locRows]) => {
        const totals = locRows.reduce((acc, r) => ({
          cc_tips: acc.cc_tips + Number(r.cc_tips || 0),
          scheduled_hours: acc.scheduled_hours + Number(r.scheduled_hours || 0),
          actual_hours: acc.actual_hours + Number(r.actual_hours || 0),
          labor_cost: acc.labor_cost + Number(r.labor_cost || 0),
        }), { cc_tips: 0, scheduled_hours: 0, actual_hours: 0, labor_cost: 0 });
        const avgRate = totals.actual_hours > 0 ? totals.labor_cost / totals.actual_hours : 0;
        const weekEnd = (() => {
          const d = new Date(weekStart + 'T12:00:00Z'); d.setUTCDate(d.getUTCDate() + 6);
          return d.toISOString().slice(0, 10);
        })();
        const prettyRange = (() => {
          const fmt = (s) => new Date(s + 'T12:00:00Z')
            .toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
          return `${fmt(weekStart)} – ${fmt(weekEnd)}`;
        })();
        return (
          <div key={weekStart} className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                            flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
              <div className="text-sm font-semibold">{prettyRange}</div>
              <div style={{ fontSize: 12, color: '#666' }}>
                Total tips {fmtMoney(totals.cc_tips)} · Sched {fmtHours(totals.scheduled_hours)} ·
                Actual {fmtHours(totals.actual_hours)} · Labor {fmtMoney(totals.labor_cost)} · Avg ${avgRate.toFixed(2)}/hr
              </div>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', fontSize: 13 }}>
                <thead>
                  <tr style={{ textAlign: 'left', borderBottom: '1px solid #e0e0e0', color: '#757575' }}>
                    <th style={{ padding: 6 }}>Location</th>
                    <th style={{ padding: 6 }}>Tips</th>
                    <th style={{ padding: 6 }}>Scheduled</th>
                    <th style={{ padding: 6 }}>Actual</th>
                    <th style={{ padding: 6 }}>Labor cost</th>
                    <th style={{ padding: 6 }}>Avg $/hr</th>
                    <th style={{ padding: 6 }}>Labor %</th>
                  </tr>
                </thead>
                <tbody>
                  {locRows.map((r) => {
                    const rate = r.actual_hours > 0 ? Number(r.labor_cost) / Number(r.actual_hours) : 0;
                    return (
                      <tr key={r.location_id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: 6 }}>{locName(r.location_id)}</td>
                        <td style={{ padding: 6 }}>{fmtMoney(r.cc_tips)}</td>
                        <td style={{ padding: 6 }}>{fmtHours(r.scheduled_hours)}</td>
                        <td style={{ padding: 6 }}>{fmtHours(r.actual_hours)}</td>
                        <td style={{ padding: 6 }}>{fmtMoney(r.labor_cost)}</td>
                        <td style={{ padding: 6 }}>${rate.toFixed(2)}</td>
                        <td style={{ padding: 6, fontWeight: 600,
                                     color: laborPct(r) == null ? '#9e9e9e' : laborPct(r) > 33 ? '#b91c1c' : '#2e7d32' }}>
                          {laborPct(r) != null ? laborPct(r).toFixed(1) + '%' : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}
